import React, { useState } from "react";
import imageCompression from "browser-image-compression";
import axios from "axios";
import PushToTalkButton from "./voice/PushToTalkButton.jsx";

function HomeRunFlow({ propertyId, postalCode }) {
  console.log("🔥 HomeRunFlow MOUNTED");

  const [step, setStep] = useState(1);

  // Issue creation state
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // Backend state
  const [issue, setIssue] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [appointment, setAppointment] = useState(null);

  const nextStep = () => setStep((s) => s + 1);
  const goToStep = (n) => setStep(n);

  // -------------------------------------------------------------
  // STABLE SECTION WRAPPER — no remounting
  // -------------------------------------------------------------
  const AnimatedSection = ({ stepNumber, current, children }) => (
    <div style={{ display: current === stepNumber ? "block" : "none" }}>
      {children}
    </div>
  );

  // -------------------------------------------------------------
  // IMAGE HANDLING
  // -------------------------------------------------------------
  async function handleImageUpload(e) {
    const files = Array.from(e.target.files);
    const compressedImages = [];
    const previews = [];

    for (const file of files) {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
      });

      const base64 = await fileToBase64(compressed);
      compressedImages.push(base64);
      previews.push(URL.createObjectURL(compressed));
    }

    setImages(compressedImages);
    setPreviewUrls(previews);
  }

  function fileToBase64(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  }

  // -------------------------------------------------------------
  // CREATE ISSUE
  // -------------------------------------------------------------
  async function handleCreateIssue() {
    try {
      const payload = {
        title: "Reported Issue",
        description,
        category: "general",
        severity: "medium",
        riskType: "unknown",
        recommendedTrade: "",
        recommendedTimeline: "0-3 months",
        photos: images,
      };

      const res = await axios.post(
        `/api/homerun/diagnosis/${propertyId}/issues`,
        payload
      );

      setIssue(res.data);
      nextStep();
    } catch (err) {
      console.error("❌ Failed to create issue:", err);
      alert("❌ Could not create issue. Check backend route.");
    }
  }

  // -------------------------------------------------------------
  // DIAGNOSIS
  // -------------------------------------------------------------
  async function handleDiagnose() {
    try {
      const res = await axios.post(
        `/api/homerun/diagnosis/${propertyId}/run`,
        { issueId: issue.id }
      );

      setDiagnosis(res.data);
      nextStep();
    } catch (err) {
      console.error("Diagnosis failed:", err);
    }
  }

  // -------------------------------------------------------------
  // LOAD VENDORS
  // -------------------------------------------------------------
  async function handleLoadVendors() {
    try {
      const res = await axios.get(
        `/api/homerun/vendors?postal=${postalCode}&type=${diagnosis?.component}`
      );

      setVendors(res.data || []);
      nextStep();
    } catch (err) {
      console.error("Vendor lookup failed:", err);
    }
  }

  // -------------------------------------------------------------
  // BOOK APPOINTMENT
  // -------------------------------------------------------------
  async function handleSchedule() {
    try {
      const timeSlot =
        selectedVendor &&
        Object.values(selectedVendor.availability)[0][0];

      const res = await axios.post(`/api/homerun/appointments`, {
        issue_id: issue.id,
        vendor_id: selectedVendor.id,
        property_id: propertyId,
        scheduled_for: timeSlot,
        notes: "Booked via Harbour Nest",
      });

      setAppointment(res.data);
      nextStep();
    } catch (err) {
      console.error("Booking failed:", err);
    }
  }

  // -------------------------------------------------------------
  // UI COMPONENTS
  // -------------------------------------------------------------
  const StepIndicator = ({ number, label }) => (
    <div
      className={`flex items-center gap-2 cursor-pointer transition-all ${
        step === number ? "text-hn-red font-semibold" : "text-gray-400"
      }`}
      onClick={() => goToStep(number)}
    >
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full border
          ${
            step === number
              ? "bg-hn-red text-white border-hn-red scale-110"
              : "border-gray-300"
          }
        `}
      >
        {number}
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );

  const Card = ({ children }) => (
    <div className="bg-white rounded-xl shadow p-6 mb-6">{children}</div>
  );

  const Button = ({ children, onClick }) => (
    <button
      className="bg-hn-red text-white px-5 py-2 rounded-md hover:bg-red-700 transition"
      onClick={onClick}
    >
      {children}
    </button>
  );

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  return (
    <div className="bg-white rounded-xl shadow p-8">
      <h2 className="text-2xl font-brand text-hn-dark mb-1">
        Fix something that looks off
      </h2>
      <p className="text-gray-500 text-sm mb-6">
        We'll diagnose the issue, match a technician, and book the repair.
      </p>

      {/* STEP INDICATORS */}
      <div className="flex items-center justify-between mb-10">
        <StepIndicator number={1} label="Describe" />
        <StepIndicator number={2} label="Diagnosis" />
        <StepIndicator number={3} label="Technician" />
        <StepIndicator number={4} label="Schedule" />
        <StepIndicator number={5} label="Booked" />
      </div>

      {/* STEP 1 — Describe */}
      <AnimatedSection stepNumber={1} current={step}>
        <Card>
          <h3 className="text-xl mb-4 text-hn-dark">What seems off?</h3>

          <PushToTalkButton onTranscript={(text) => setDescription(text)} />

          <textarea
            className="w-full border border-gray-300 p-3 rounded-md font-sans mt-4"
            rows={3}
            placeholder="You can also type here..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="mt-4">
            <label className="text-hn-dark text-sm mb-2 block">
              Add Photos (optional)
            </label>

            <div
              className="border border-gray-300 border-dashed rounded-md p-6 text-center cursor-pointer hover:bg-gray-50"
              onClick={() => document.getElementById("issue-photos").click()}
            >
              <p className="text-gray-500 text-sm">
                Tap to upload photos
              </p>
            </div>

            <input
              id="issue-photos"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageUpload}
            />

            {previewUrls.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {previewUrls.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt="preview"
                    className="w-full h-24 object-cover rounded-md shadow"
                  />
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleCreateIssue}>Next</Button>
        </Card>
      </AnimatedSection>

      {/* STEP 2 — Diagnosis */}
      <AnimatedSection stepNumber={2} current={step}>
        <Card>
          <h3 className="text-xl mb-4 text-hn-dark">
            Diagnosing your issue…
          </h3>
          <Button onClick={handleDiagnose}>Run Diagnosis</Button>
        </Card>
      </AnimatedSection>

      {/* STEP 3 — Vendors */}
      <AnimatedSection stepNumber={3} current={step}>
        <Card>
          <h3 className="text-xl mb-4 text-hn-dark">Recommended Technicians</h3>
          {diagnosis && (
            <p className="text-gray-600 text-sm mb-3">
              Detected: <strong>{diagnosis.component}</strong>
            </p>
          )}
          <Button onClick={handleLoadVendors}>Load Technicians</Button>
        </Card>
      </AnimatedSection>

      {/* STEP 4 — Select Technician */}
      <AnimatedSection stepNumber={4} current={step}>
        <Card>
          <h3 className="text-xl mb-4 text-hn-dark">Select a Technician</h3>

          {vendors.map((v) => (
            <div
              key={v.id}
              className={`border rounded-md p-4 mb-3 cursor-pointer transition
                ${
                  selectedVendor?.id === v.id
                    ? "border-hn-red bg-red-50"
                    : "border-gray-200"
                }`}
              onClick={() => setSelectedVendor(v)}
            >
              <p className="text-lg text-hn-dark">{v.name}</p>
              <p className="text-gray-600 text-sm">
                ${v.price_low}–{v.price_high}
              </p>
            </div>
          ))}

          {selectedVendor && (
            <Button onClick={handleSchedule}>Schedule</Button>
          )}
        </Card>
      </AnimatedSection>

      {/* STEP 5 — Confirmation */}
      <AnimatedSection stepNumber={5} current={step}>
        <Card className="bg-green-50 border border-green-300">
          <h3 className="text-xl text-hn-dark">Your repair is booked</h3>

          <p className="mt-2">
            Technician: <strong>{selectedVendor?.name}</strong>
          </p>
          <p>
            Scheduled for: <strong>{appointment?.scheduled_for}</strong>
          </p>
        </Card>
      </AnimatedSection>
    </div>
  );
}

export default React.memo(HomeRunFlow);
